/**
 * @file     metaPixel.ts
 * @purpose  Meta (Facebook/Instagram) Pixel calls. The boot snippet installs a
 *           queueing `fbq` stub before hydration. A call that arrives first
 *           installs that same stub, so the event is kept, not dropped.
 */
import { isSignaturePublicPath } from '@/core/config/signaturePaths';
import { env } from '@/core/config/env';
import { browserGpcEnabled } from './gpc';
import { CONSENT_COOKIE, consentReaderSnippet, optInRegionFromCookies, parseConsent } from './consent';

type FbqArgs = unknown[];

interface Fbq {
  (command: 'track', event: string, params?: Record<string, unknown>, options?: { eventID: string }): void;
  (command: 'init', pixelId: string): void;
  (command: 'consent', action: 'grant' | 'revoke'): void;
  callMethod?: (...args: unknown[]) => void;
  queue: FbqArgs[];
  push: Fbq;
  loaded: boolean;
  version: string;
}

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
    /** Set once `fbq('init')` has been queued for this page. */
    __reltaMetaInit?: boolean;
    /** Set once the landing PageView has been queued. */
    __reltaMetaPageView?: boolean;
    /** Set once the deferred fbevents.js loader is registered. */
    __reltaMetaLoader?: boolean;
  }
}

/** Digits-only pixel id, or '' when analytics or the id is off. Safe to inline. */
export function activeMetaPixelId(): string {
  if (!env.enableAnalytics || !/^\d+$/.test(env.metaPixelId)) return '';
  return env.metaPixelId;
}

function installStub(w: Window): Fbq {
  const n = function (...args: unknown[]) {
    if (typeof n.callMethod === 'function') n.callMethod.apply(n, args);
    else n.queue.push(args);
  } as Fbq;
  n.queue = [];
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  w.fbq = n;
  if (!w._fbq) w._fbq = n;
  return n;
}

/** fbevents.js stays off the critical path. The stub's queue flushes when it arrives. */
function scheduleLibrary(w: Window): void {
  if (w.__reltaMetaLoader) return;
  w.__reltaMetaLoader = true;
  let loaded = false;
  const load = () => {
    if (loaded || typeof document === 'undefined') return;
    loaded = true;
    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(s);
  };
  w.addEventListener('relta:lead-submitted', load, { once: true });
  for (const eventName of ['scroll', 'click', 'touchstart', 'mousemove'] as const) {
    w.addEventListener(eventName, load, { once: true, passive: true });
  }
  w.setTimeout(load, 8000);
}

function storedMarketingChoice(): boolean | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_COOKIE}=([^;]*)`));
  const choice = parseConsent(m ? m[1] : null);
  return choice ? choice.marketing : null;
}

/**
 * GPC always wins. Otherwise an explicit "no" from the banner stops the pixel,
 * and in an opt-in region (Quebec) or on a signing page so does having made
 * no choice yet.
 */
function metaAllowed(): boolean {
  if (browserGpcEnabled()) return false;
  const choice = storedMarketingChoice();
  if (choice !== null) return choice;
  return !optInRegionFromCookies(document.cookie) && !isSignaturePublicPath(window.location.pathname);
}

function getFbq(): Fbq | null {
  if (typeof window === 'undefined' || !metaAllowed()) return null;
  const pixelId = activeMetaPixelId();
  if (!pixelId) return null;
  if (typeof window.fbq !== 'function') {
    const n = installStub(window);
    if (!window.__reltaMetaInit) {
      window.__reltaMetaInit = true;
      n('init', pixelId);
    }
    scheduleLibrary(window);
  }
  return window.fbq ?? null;
}

/**
 * Standard `Lead` event for Instagram and Facebook ad optimisation. `eventId`
 * is the lead's transaction id, so a later Conversions API leg can dedupe
 * against this browser leg.
 */
export function trackMetaLead(input: { eventId: string; value: number; currency: string; leadType: string }): void {
  getFbq()?.('track', 'Lead', { value: input.value, currency: input.currency, content_category: input.leadType }, { eventID: input.eventId });
}

/** Client-side navigations in the App Router never reload the page, so each one is reported here. */
export function trackMetaPageView(): void {
  getFbq()?.('track', 'PageView');
}

/**
 * Marketing was just granted. A pixel that already booted resumes; one the
 * boot snippet skipped (stored "no") boots now and reports this page.
 */
export function grantMetaPixel(): void {
  if (typeof window === 'undefined' || !metaAllowed()) return;
  const booted = window.__reltaMetaInit === true;
  const fbq = getFbq();
  if (!fbq) return;
  if (booted) {
    fbq('consent', 'grant');
    return;
  }
  if (!window.__reltaMetaPageView) {
    window.__reltaMetaPageView = true;
    fbq('track', 'PageView');
  }
}

/** Marketing was just withdrawn: a booted pixel stops sending. getFbq() already refuses new calls. */
export function revokeMetaPixel(): void {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  window.fbq('consent', 'revoke');
}

/**
 * Inline boot snippet for the root layout. It runs from the initial HTML,
 * before hydration, so the queue exists before any App Router effect. The
 * network fetch of fbevents.js stays on the same deferred triggers as gtag.
 * Returns null when the pixel is not configured.
 */
export function metaPixelBootScript(): string | null {
  const pixelId = activeMetaPixelId();
  if (!pixelId) return null;
  return `(function(w){
    if (w.navigator && w.navigator.globalPrivacyControl === true) return;
    ${consentReaderSnippet()}
    if (reltaConsent ? !reltaConsent.m : reltaOptIn) return;
    if (!w.fbq) {
      var n = w.fbq = function(){ n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!w._fbq) w._fbq = n;
      n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
    }
    if (!w.__reltaMetaInit) {
      w.__reltaMetaInit = true;
      w.fbq('init', '${pixelId}');
    }
    if (!w.__reltaMetaPageView) {
      w.__reltaMetaPageView = true;
      w.fbq('track', 'PageView');
    }
    if (w.__reltaMetaLoader) return;
    w.__reltaMetaLoader = true;
    var loaded = false;
    function load(){
      if (loaded) return; loaded = true;
      var s = document.createElement('script');
      s.async = true;
      s.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(s);
    }
    w.addEventListener('relta:lead-submitted', load, { once: true });
    ['scroll','click','touchstart','mousemove'].forEach(function(e){
      w.addEventListener(e, load, { once: true, passive: true });
    });
    setTimeout(load, 8000);
  })(window);`;
}
